import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { AccessApiPolicyService } from '../../../shared/access-api/policy/access-api-policy.service';
import * as CentralPolicyUsageActions from './central-policy-usage.actions';

@Injectable()
export class CentralPolicyUsageEffects {
  private readonly action$ = inject(Actions);
  private readonly accessApiPolicyService = inject(AccessApiPolicyService);

  public onLoadCentralPolicyUsageGroupSummary$ = createEffect(() => {
    return this.action$.pipe(
      ofType(CentralPolicyUsageActions.loadCentralPolicyUsageGroupSummary),
      switchMap((action) =>
        this.accessApiPolicyService.getGroupCentralPolicyUsageSummary(action.groupId, action.groupComplianceMode).pipe(
          map((groupSummary) =>
            CentralPolicyUsageActions.setCentralPolicyUsageGroupSummary({
              groupSummary,
            }),
          ),
          catchError((error: HttpErrorResponse) => {
            const groupId = action.groupId;
            const groupComplianceMode = action.groupComplianceMode;
            if (error.status == 404) {
              return of(CentralPolicyUsageActions.loadCentralPolicyUsageGroupSummaryNotFound({ groupId, groupComplianceMode }));
            }
            return of(CentralPolicyUsageActions.loadCentralPolicyUsageGroupSummaryError({ groupId, groupComplianceMode }));
          }),
        ),
      ),
    );
  });

  public onLoadCentralPolicyUsageGroupDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType(CentralPolicyUsageActions.loadCentralPolicyUsageGroupDetails),
      switchMap((action) =>
        this.accessApiPolicyService.getGroupCentralPolicyUsageDetails(action.groupId, action.groupComplianceMode).pipe(
          map((groupDetails) =>
            CentralPolicyUsageActions.setCentralPolicyUsageGroupDetails({
              groupDetails,
            }),
          ),
          catchError(() => {
            return of(
              CentralPolicyUsageActions.loadCentralPolicyUsageGroupDetailsError({
                groupId: action.groupId,
                groupComplianceMode: action.groupComplianceMode,
              }),
            );
          }),
        ),
      ),
    );
  });

  public onLoadCentralPolicyKeyGroupDetails$ = createEffect(() => {
    return this.action$.pipe(
      ofType(CentralPolicyUsageActions.loadCentralPolicyKeyGroupDetails),
      mergeMap((action) => {
        return this.accessApiPolicyService.getPolicyKeyGroupDetails(action.groupId, action.policyKey, action.groupComplianceMode).pipe(
          map((singleSummary) =>
            CentralPolicyUsageActions.setCentralPolicyKeyGroupDetails({
              groupPolicyKeySummary: singleSummary,
            }),
          ),
          catchError((error) => {
            if (error.status === 404) {
              return of(
                CentralPolicyUsageActions.loadCentralPolicyKeyGroupDetailsNotFound({
                  groupId: action.groupId,
                  policyKey: action.policyKey,
                  groupComplianceMode: action.groupComplianceMode,
                }),
              );
            }
            return of(
              CentralPolicyUsageActions.loadCentralPolicyKeyGroupDetailsError({
                groupId: action.groupId,
                policyKey: action.policyKey,
                groupComplianceMode: action.groupComplianceMode,
              }),
            );
          }),
          takeUntil(
            this.action$.pipe(
              ofType(CentralPolicyUsageActions.loadCentralPolicyKeyGroupDetails),
              filter((newAction) => newAction.groupId === action.groupId && newAction.policyKey === action.policyKey),
            ),
          ),
        );
      }),
    );
  });
}
